/**
 * De onde veio o número — a informação que o app existe para dar.
 *
 * Este arquivo tem **dois** selos de propósito, porque a API chama de
 * "confiança" duas coisas diferentes e é melhor ver as duas lado a lado do
 * que descobrir a colisão no meio de uma tela:
 *
 * - `SeloDeConfianca` recebe o **objeto** `{ nivel: 1|2|3, porque }` que vem
 *   com o resultado da simulação, e responde "de onde veio o número desta
 *   conta".
 * - `SeloDaProcedencia` recebe a **string** `"norma" | "referencia" |
 *   "mercado" | "estimativa"` que vem com cada premissa e cada item do
 *   catálogo, e responde "de onde veio este dado". O vocabulário está em
 *   `nucleo/confianca.js`.
 *
 * Trocar um pelo outro não dá erro: o `nivel` de um objeto é `undefined` num
 * `ESTILO[string]`, e o selo cai no ramo de reserva mostrando "Estimativa de
 * tabela" para um dado de norma. Por isso os dois moram juntos.
 *
 * Todo resultado chega com um nível de confiança, e ele não é decoração: a
 * mesma conta vale para pedir financiamento ou não vale, dependendo de o
 * potencial de metano ter saído de ensaio deste material ou de uma tabela
 * alemã. A API já rebaixa o nível quando empresta o número do catálogo; aqui
 * isso vira cor e palavra, porque o produtor não lê `"nivel": 1`.
 *
 * A cor âmbar do catálogo não quer dizer "erro". Quer dizer "estimativa de
 * tabela" — e o passo seguinte, que é mandar o dejeto para análise, aparece
 * junto, porque apontar a lacuna sem apontar a saída é só dizer não.
 */
import { procedencia } from '../nucleo/confianca.js';

const ESTILO = {
  3: { classe: 'selo--ensaio', simbolo: '●●●', curto: 'Ensaio medido' },
  2: { classe: 'selo--bancada', simbolo: '●●○', curto: 'Bancada convertida' },
  1: { classe: 'selo--catalogo', simbolo: '●○○', curto: 'Estimativa de tabela' },
};

export function SeloDeConfianca({ confianca, detalhado = false }) {
  if (!confianca) return null;
  const e = ESTILO[confianca.nivel] ?? ESTILO[1];

  if (!detalhado) {
    return (
      <span className={`selo ${e.classe}`}>
        <span aria-hidden="true" className="selo__pontos">
          {e.simbolo}
        </span>
        {e.curto}
      </span>
    );
  }

  return (
    <div className={`confianca confianca--${confianca.nivel}`}>
      <span className={`selo ${e.classe}`}>
        <span aria-hidden="true" className="selo__pontos">
          {e.simbolo}
        </span>
        {e.curto}
      </span>
      <p className="confianca__porque">{confianca.porque}.</p>
    </div>
  );
}

/**
 * A procedência de um dado — a string de quatro valores.
 *
 * Com `detalhado`, mostra também o que a procedência significa e o que fazer
 * a respeito. O "o que fazer" só existe para `mercado` (confira a data) e
 * `estimativa` (mande analisar): nos dois fortes não há nada a fazer, e
 * inventar uma recomendação para eles ensinaria o leitor a não ler as duas
 * que importam.
 */
export function SeloDaProcedencia({ valor, detalhado = false }) {
  const p = procedencia(valor);
  if (!p) return null;

  const selo = <span className={`selo ${p.classe}`}>{p.curto}</span>;
  if (!detalhado) return selo;

  return (
    <div className="procedencia">
      {selo}
      <p className="confianca__porque">
        {p.significa}
        {p.oQueFazer ? ` — ${p.oQueFazer}` : ''}.
      </p>
    </div>
  );
}
